import React, { useState, useEffect } from 'react'
import {
  ArrowLeft,
  Save,
  RotateCcw
} from 'lucide-react'
import Breadcrumb from '../../components/Common/Breadcrumb'
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import assetLocationMasterService from '../../services/assetLocationMasterService'
import assetDropdownService, { ASSET_DROPDOWN_FALLBACKS, optionsFromDropdown } from '../../services/assetDropdownService'
import { labelStyle, inputStyle, textareaStyle, gridStyle, sectionStyle, fullWidthStyle, requiredStyle } from './assetFormStyles'

const emptyForm = {
  locationCode: '',
  locationName: '',
  locationDescription: '',
  locationType: 'Office',
  address: {
    line1: '',
    city: '',
    state: '',
    country: '',
    postalCode: ''
  },
  status: 'Active',
  notes: ''
}

const AssetLocationMasterForm = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const isEditMode = location.pathname.includes('/edit')
  const isViewMode = location.pathname.includes('/view')
  const locationData = location.state?.locationData
  const recordId = locationData?._id || searchParams.get('id')

  const [formData, setFormData] = useState(emptyForm)
  const [locationTypeOptions, setLocationTypeOptions] = useState(
    () => optionsFromDropdown(null, 'locationType', ASSET_DROPDOWN_FALLBACKS.locationType)
  )
  const [statusOptions, setStatusOptions] = useState(
    () => optionsFromDropdown(null, 'masterStatus', ASSET_DROPDOWN_FALLBACKS.masterStatus)
  )
  const [isLoadingCode, setIsLoadingCode] = useState(true)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    const loadDropdownOptions = async () => {
      try {
        const response = await assetDropdownService.getOptions()
        if (response.success && response.data) {
          const { dropdowns } = response.data
          setLocationTypeOptions(optionsFromDropdown(dropdowns, 'locationType', ASSET_DROPDOWN_FALLBACKS.locationType))
          setStatusOptions(optionsFromDropdown(dropdowns, 'masterStatus', ASSET_DROPDOWN_FALLBACKS.masterStatus))
        }
      } catch (error) {
        console.error('Error loading location dropdown options:', error)
      }
    }
    loadDropdownOptions()
  }, [])

  // Fetch next location code (only for new records)
  useEffect(() => {
    if (!isEditMode && !isViewMode) {
      const fetchLocationCode = async () => {
        try {
          setIsLoadingCode(true)
          const response = await assetLocationMasterService.getNextLocationCode()
          if (response.success && response.data) {
            setFormData(prev => ({ ...prev, locationCode: response.data.locationCode || 'LOC0001' }))
          }
        } catch (error) {
          console.error('Error fetching location code:', error)
          setFormData(prev => ({ ...prev, locationCode: 'LOC0001' }))
        } finally {
          setIsLoadingCode(false)
        }
      }
      fetchLocationCode()
    } else {
      setIsLoadingCode(false)
    }
  }, [isEditMode, isViewMode])

  // Load record data if editing or viewing
  useEffect(() => {
    if ((isEditMode || isViewMode) && recordId) {
      loadLocationData(recordId)
    }
  }, [isEditMode, isViewMode, recordId])

  const loadLocationData = async (id) => {
    try {
      setIsLoading(true)
      const response = await assetLocationMasterService.getLocationById(id)
      if (response.success && response.data) {
        const record = response.data
        setFormData({
          locationCode: record.locationCode || '',
          locationName: record.locationName || '',
          locationDescription: record.locationDescription || '',
          locationType: record.locationType || 'Office',
          address: {
            line1: record.address?.line1 || '',
            city: record.address?.city || '',
            state: record.address?.state || '',
            country: record.address?.country || '',
            postalCode: record.address?.postalCode || ''
          },
          status: record.status || 'Active',
          notes: record.notes || ''
        })
      }
    } catch (error) {
      console.error('Error loading asset location data:', error)
      toast.error('Failed to load asset location data')
    } finally {
      setIsLoading(false)
    }
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target

    if (name.startsWith('address.')) {
      const addressField = name.split('.')[1]
      setFormData(prev => ({
        ...prev,
        address: { ...prev.address, [addressField]: value }
      }))
    } else {
      setFormData(prev => ({ ...prev, [name]: value }))
    }
  }

  const handleSave = async () => {
    try {
      if (!formData.locationName) {
        toast.error('Please fill in Location Name')
        return
      }

      setIsLoading(true)

      if (isEditMode && recordId) {
        const response = await assetLocationMasterService.updateLocation(recordId, formData)
        if (response.success) {
          toast.success('Asset location updated successfully!')
          navigate('/assets/location-master')
        } else {
          toast.error(response.message || 'Failed to update asset location')
        }
      } else {
        const response = await assetLocationMasterService.createLocation(formData)
        if (response.success) {
          toast.success('Asset location created successfully!')
          navigate('/assets/location-master')
        } else {
          toast.error(response.message || 'Failed to create asset location')
        }
      }
    } catch (error) {
      console.error('Error saving asset location:', error)
      if (error.response?.status === 400) {
        toast.error(error.response.data?.message || 'Invalid data provided')
      } else if (!error._errorHandled) {
        toast.error('Failed to save asset location')
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleReset = () => {
    if ((isEditMode || isViewMode) && recordId) {
      loadLocationData(recordId)
    } else {
      setFormData({ ...emptyForm, locationCode: formData.locationCode })
    }
  }

  return (
    <div className="sales-dashboard">
      <Breadcrumb
        items={[
          { label: 'Assets', path: '/assets' },
          { label: 'Locations', path: '/assets/location-master' },
          { label: isEditMode ? 'Edit' : isViewMode ? 'View' : 'New', isCurrent: true }
        ]}
      />

      <div className="page-header">
        <div className="page-title">
          <h1>{isEditMode ? 'Edit Location' : isViewMode ? 'View Location' : 'New Location'}</h1>
          <p>{isViewMode ? 'View location details' : isEditMode ? 'Update location information' : 'Create a new asset location'}</p>
        </div>
        <div className="page-actions">
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => navigate('/assets/location-master')}>
            <ArrowLeft size={16} />
            Back
          </button>
          {!isViewMode && (
            <>
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleReset}>
                <RotateCcw size={16} />
                Reset
              </button>
              <button type="button" className="btn-primary" onClick={handleSave} disabled={isLoading}>
                <Save size={16} />
                {isLoading ? 'Saving...' : 'Save'}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="form-container">
        <div className="sac-form-header">
          <h2>Location Entry</h2>
        </div>
        <form className="form-content">
          <div className="section-header">
            <h3>Basic Information</h3>
          </div>
          <div style={sectionStyle}>
            <div style={gridStyle}>
              <div>
                <label style={labelStyle}>
                  Location Code <span style={requiredStyle}>*</span>
                </label>
                <input
                  type="text"
                  name="locationCode"
                  value={formData.locationCode}
                  onChange={handleInputChange}
                  disabled={isViewMode || isEditMode || isLoadingCode}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>
                  Location Name <span style={requiredStyle}>*</span>
                </label>
                <input
                  type="text"
                  name="locationName"
                  value={formData.locationName}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  required
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>Location Type</label>
                <select
                  name="locationType"
                  value={formData.locationType}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  style={inputStyle}
                >
                  {locationTypeOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label style={labelStyle}>Status</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  style={inputStyle}
                >
                  {statusOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              <div style={fullWidthStyle}>
                <label style={labelStyle}>Description</label>
                <textarea
                  name="locationDescription"
                  value={formData.locationDescription}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  rows={3}
                  style={textareaStyle}
                />
              </div>
            </div>
          </div>

          <div className="form-section-divider">
            <div className="section-header">
              <h3>Address</h3>
            </div>
          </div>
          <div style={sectionStyle}>
            <div style={gridStyle}>
              <div style={fullWidthStyle}>
                <label style={labelStyle}>Address Line 1</label>
                <input
                  type="text"
                  name="address.line1"
                  value={formData.address.line1}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>City</label>
                <input
                  type="text"
                  name="address.city"
                  value={formData.address.city}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>State</label>
                <input
                  type="text"
                  name="address.state"
                  value={formData.address.state}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>Country</label>
                <input
                  type="text"
                  name="address.country"
                  value={formData.address.country}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>Postal Code</label>
                <input
                  type="text"
                  name="address.postalCode"
                  value={formData.address.postalCode}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  style={inputStyle}
                />
              </div>
            </div>
          </div>

          <div className="form-section-divider">
            <div className="section-header">
              <h3>Additional Information</h3>
            </div>
          </div>
          <div style={sectionStyle}>
            <div>
              <label style={labelStyle}>Notes</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                disabled={isViewMode}
                rows={4}
                style={textareaStyle}
              />
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}

export default AssetLocationMasterForm
